import dayjs from "dayjs";
import { connect } from "react-redux";
import "../../assets/css/promote.css?v1.3";
import AllFunnelCard from "./AllFunnelCard";
import { useNavigate } from "react-router-dom";
import OtherFunnelsCard from "./OtherFunnelsCard";
import React, { useEffect, useState } from "react";
import { toastrOnTopCenter } from "../../utils/toastr";
import { getFunnelLinks } from "../../redux/selectors";
import { funnelImages } from "../../utils/portalImages";
import { LEVEL_ADMIN } from "../../utils/userPermissions";
import { usePermissions } from "../../context/permissionsContext";
import AuthenticatedLayout from "../../layouts/AuthenticatedLayout";
import DateRangeFilter from "../affiliate/components/DateRangeFilter";
import { Select, MenuItem, InputLabel, FormControl } from "@mui/material";

const dateOption = [
  { key: "all", value: "All Time" },
  { key: "today", value: "Today" },
  { key: "yesterday", value: "Yesterday" },
  { key: "last_seven", value: "Last 7 Days" },
  { key: "last_fourteen", value: "Last 14 Days" },
  { key: "this_month", value: "This Month" },
  { key: "last_month", value: "Last Month" },
  { key: "last_three_month", value: "Last 3 Months" },
  { key: "last_six_month", value: "Last 6 Months" },
  { key: "last_twelve_month", value: "Last 12 Months" },
  { key: "this_year", value: "This Year" },
  { key: "last_year", value: "Last Year" },
  { key: "custom", value: "Custom" },
];

const funnelConfigs = [
  { key: "master_class_funnel", name: "1 Hour Master Class", type: "master" },
  { key: "blueprint_funnel", name: "Blueprint", type: "blueprint" },
  {
    key: "live_opportunity_call_funnel",
    name: "Live Opportunity Call",
    type: "live_opportunity_call",
  },
  {
    key: "annual_special_funnel",
    name: "Annual Special",
    type: "annual_special",
  }, 
  {
    key: "five_month_intro_plan",
    name: "5 Month Intro Plan",
    type: "five_month_intro_plan",
  }, 
  {
    key: "ninety_day_intro_plan",
    name: "90 Days Intro Plan",
    type: "ninety_day_intro_plan",
  },
  {
    key: "instant_signup_payment",
    name: "Instant Signup Payment",
    type: "instant_signup_payment",
  },
  {
    key: "dollar_trial_funnel",
    name: "$1 Trial",
    type: "dollar_trial",
  },

  {
    key: "free_registration_no_card",
    name: "Free Registration",
    type: "free_registration_no_card",
  },
  {
    key: "card_free_trial_funnel",
    name: "7 Days Free Trial",
    type: "card_free_trial",
  },
  
  {
    key: "exclusive_member",
    name: "Exclusive Member - Admin",
    type: "exclusive_member",
    adminOnly: true,
  },
  { key: "guest_user_funnel", name: "Guest User", type: "guest_user" },
];

const Funnel = ({ funnel }) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState("all");
  const [showDateModal, setShowDateModal] = useState(false);
  const [filter, setFilter] = useState({
    period: "all",
    start_date: null,
    end_date: null,
  });

  useEffect(() => {
    if (filter.period === "custom") setShowDateModal(true);
  }, [filter.period]);

  const funnelImagesMap = {
    optin: funnelImages.funnel_optin,
    vsl: funnelImages.funnel_webinar,
    orderforms: funnelImages.funnel_checkout,
    thankyou: funnelImages.funnel_thankyou,
  };

  const handleCopyClick = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => toastrOnTopCenter("Link copied!", "success"))
      .catch(() =>
        toastrOnTopCenter("Something went wrong, try again", "error")
      );
  };

  const getFunnelData = (field = "welcome", sub_name) =>
    funnelConfigs
      .filter((cfg) => {
        // Filter out blueprint from opt-in and order forms, only show in VSL
        if (cfg.key === "blueprint_funnel") {
          return sub_name === "VSL"; // Only show blueprint in VSL tab
        }
        return !cfg.adminOnly || hasPermission(LEVEL_ADMIN);
      })
      .map((cfg) => {
        // For blueprint in VSL, use welcome link (which points to /blueprint) instead of webinar
        let link = funnel[cfg.key]?.[field];
        if (cfg.key === "blueprint_funnel" && sub_name === "VSL") {
          link = funnel[cfg.key]?.welcome; // Use welcome link for blueprint in VSL tab
        }
        return {
          name: cfg.name,
          sub_name,
          link: link,
          funnel_type: cfg.type,
          funnel_images:
            funnelImagesMap[sub_name?.toLowerCase().replace(/\s/g, "")] || null,
        };
      });

  const tabMap = {
    all: [
      ...getFunnelData("welcome", "Opt In"),
      ...getFunnelData("webinar", "VSL"),
      ...getFunnelData("checkout", "Order Forms"),
    ],
    optin: getFunnelData("welcome", "Opt In"),
    vsl_pages: getFunnelData("webinar", "VSL"),
    order_forms: getFunnelData("checkout", "Order Forms"),
  };

  const getTabCount = (label, tabItems = []) => {
    // Filter out items that would be hidden based on the logic in both AllFunnelCard and OtherFunnelsCard
    const visibleItems = tabItems.filter((item) => {
      // Logic for AllFunnelCard (used in "All" tab)
      const shouldHideInAllFunnelCard = 
        ((item.sub_name === "Opt In" || item.sub_name === "VSL") &&
          (item.name === "Guest User" ||
            item.name === "7 Days Free Trial" ||
            item.name === "$1 Trial" ||
            item.name === "Annual Special" ||
            item.name === "90 Days Intro Plan" ||
            item.name === "Instant Signup Payment" ||
            item.name === "5 Month Intro Plan" ||
            item.name === "Free Registration")) ||
        (item.sub_name === "Order Forms" &&
          (item.name === "1 Hour Master Class" ||
            item.name === "Live Opportunity Call"));

      // Logic for OtherFunnelsCard (used in individual tabs)
      const shouldHideInOtherFunnelsCard = 
        ((item.sub_name === "Opt In" || item.sub_name === "VSL") &&
          (item.name === "Guest User" ||
            item.name === "7 Days Free Trial" ||
            item.name === "$1 Trial")) ||
        (item.sub_name === "Order Forms" &&
          (item.name === "1 Hour Master Class" ||
            item.name === "Live Opportunity Call")) ||
        ((item.sub_name === "Opt In" || item.sub_name === "VSL") &&
          (item.name === "Annual Special" ||
            item.name === "90 Days Intro Plan" ||
            item.name === "Free Registration" ||
            item.name === "5 Month Intro Plan" ||
            item.name === "Instant Signup Payment"));

      // Use the appropriate hiding logic based on the tab
      const shouldHide = label === "All" ? shouldHideInAllFunnelCard : shouldHideInOtherFunnelsCard;
      
      return !shouldHide;
    });

    return visibleItems.length;
  };

  const tabs = [
    { key: "all", label: "All", card: AllFunnelCard },
    { key: "optin", label: "Opt In Pages", card: OtherFunnelsCard },
    { key: "vsl_pages", label: "VSL Pages", card: OtherFunnelsCard },
    { key: "order_forms", label: "Order Forms", card: OtherFunnelsCard },
  ];

  const toggleDateFilterModal = () => {
    setShowDateModal(false);
    document.body.classList.remove("modal-open");
  };

  const handleDateFilterChange = (start_date, end_date) => {
    const formattedEndDate = dayjs(end_date).format("YYYY/MM/DD");
    const formattedStartDate = dayjs(start_date).format("YYYY/MM/DD");
    setFilter({
      period: "custom",
      end_date: formattedEndDate,
      start_date: formattedStartDate,
    });
  };

  return (
    <div className="row justify-content-center">
      <div className="col-lg-12 col-12">
        <button
          onClick={() => navigate("/affiliate-links/setting")}
          className="btn btn-white header-button text-xs text-nowrap rounded-pill px-3 py-2 mr-0"
        >
          <i className="fa fa-gear pr-1" /> Affiliate Settings
        </button>

        <div className="card">
          <div className="card-body pb-2 px-0">
            <div
              className={`px-3 mr-1 ${
                activeTab === "all" || activeTab === "optin" || "order_forms"
                  ? "d-space-between"
                  : ""
              }`}
            >
              <div className="d-flex">
                {tabs.map(({ key, label }) => (
                  <div
                    key={key}
                    className={`d-center ml-3 cursor-pointer ${
                      activeTab === key ? "font-weight-bold" : ""
                    }`}
                    style={{ borderRight: "2px solid #f0f2f5" }}
                  >
                    <span
                      className={`text-xs text-default px-2 p-1 mr-2 ${
                        activeTab === key ? "bg-lighter rounded" : ""
                      }`}
                      onClick={() => setActiveTab(key)}
                    >
                      {label}{" "}
                      <span className="text-white bg-default px-2 rounded">
                        {getTabCount(label, tabMap[key])}
                      </span>
                    </span>
                  </div>
                ))}
              </div>

              {(activeTab === "all" ||
                activeTab === "optin" ||
                activeTab === "order_forms") && (
                <FormControl
                  size="small"
                  sx={{ minWidth: 130, fontSize: "10px" }}
                >
                  <InputLabel id="date-range-label" sx={{ fontSize: "12px" }}>
                    Date Range
                  </InputLabel>
                  <Select
                    labelId="date-range-label"
                    id="date-range"
                    value={filter.period}
                    label="Date Range"
                    onChange={(e) => {
                      const selected = e.target.value;
                      setFilter((prev) => ({
                        ...prev,
                        period: selected,
                        ...(selected !== "custom" && {
                          start_date: null,
                          end_date: null,
                        }),
                      }));
                      if (selected === "custom") setShowDateModal(true);
                    }}
                    sx={{ fontSize: "12px" }}
                  >
                    {dateOption.map((opt) => (
                      <MenuItem
                        key={opt.key}
                        value={opt.key}
                        sx={{ fontSize: "12px" }}
                      >
                        {opt.value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </div>

            {tabMap[activeTab].map((item, idx) => {
              const CardComponent =
                tabs.find((tab) => tab.key === activeTab)?.card ||
                OtherFunnelsCard;
              return (
                <CardComponent
                  key={idx}
                  item={item}
                  filter={filter}
                  tab={activeTab}
                  handleCopyClick={handleCopyClick}
                />
              );
            })}
          </div>
        </div>
      </div>
      {showDateModal && filter.period === "custom" && (
        <DateRangeFilter
          onDateChange={handleDateFilterChange}
          toggleDateFilterModal={toggleDateFilterModal}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  funnel: getFunnelLinks(state),
});

export default connect(mapStateToProps)(AuthenticatedLayout(Funnel));
